import axios from "axios";

// Klien API terpusat untuk backend Koperasi (Express + mysql2).
const baseURL = process.env.REACT_APP_API_URL || "http://localhost:4000/api";

export const api = axios.create({ baseURL, timeout: 15000 });

// URL absolut untuk berkas yang dilayani backend di /uploads (di luar prefix /api).
export const fileUrl = (p) => (!p ? "" : /^https?:/.test(p) ? p : `${baseURL.replace(/\/api\/?$/, "")}${p}`);

const TOKEN_KEY = "kd_token";

export const tokenStore = {
  get: () => localStorage.getItem(TOKEN_KEY),
  set: (t) => localStorage.setItem(TOKEN_KEY, t),
  clear: () => localStorage.removeItem(TOKEN_KEY),
};

// Sisipkan Bearer token otomatis pada setiap request.
api.interceptors.request.use((config) => {
  const token = tokenStore.get();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Normalisasi pesan error dari backend.
export function apiError(err) {
  return err?.response?.data?.error || err?.message || "Terjadi kesalahan jaringan";
}

// ---- Endpoint helpers ----
export const AuthAPI = {
  login: (identifier, password) => api.post("/auth/login", { identifier, password }).then((r) => r.data),
  register: (payload) => api.post("/auth/register", payload).then((r) => r.data),
  // Tagihan Midtrans biaya pendaftaran (tanpa login): { status, token, redirect_url, ref }.
  registrationPayment: (ref) => api.post(`/auth/registration-payment/${ref}`).then((r) => r.data),
  me: () => api.get("/auth/me").then((r) => r.data),
  updateMe: (payload) => api.patch("/auth/me", payload).then((r) => r.data.user),
};

export const ProductAPI = {
  list: (params) => api.get("/products", { params }).then((r) => r.data.products),
  categories: () => api.get("/products/categories").then((r) => r.data.categories),
  get: (id) => api.get(`/products/${id}`).then((r) => r.data.product),
  create: (payload) => api.post("/products", payload).then((r) => r.data.product),
  update: (id, payload) => api.patch(`/products/${id}`, payload).then((r) => r.data.product),
};

export const CartAPI = {
  quote: (items) => api.post("/cart/quote", { items }).then((r) => r.data),
  // Keranjang persisten (tabel carts / cart_items)
  get: () => api.get("/cart").then((r) => r.data),
  addItem: (product_id, quantity = 1) => api.post("/cart/items", { product_id, quantity }).then((r) => r.data),
  setItem: (productId, quantity) => api.patch(`/cart/items/${productId}`, { quantity }).then((r) => r.data),
  removeItem: (productId) => api.delete(`/cart/items/${productId}`).then((r) => r.data),
  clear: () => api.delete("/cart").then((r) => r.data),
};

export const OrderAPI = {
  checkout: (payload) => api.post("/orders/checkout", payload).then((r) => r.data),
  mine: () => api.get("/orders/mine").then((r) => r.data.orders),
  confirmDelivery: (id) => api.post(`/orders/${id}/confirm-delivery`).then((r) => r.data),
  // Midtrans Snap: token untuk (bayar ulang) pesanan PENDING, dan cek status setelah popup ditutup.
  midtransToken: (id) => api.post(`/orders/${id}/midtrans`).then((r) => r.data.payment),
  midtransSync: (id) => api.post(`/orders/${id}/midtrans/sync`).then((r) => r.data.order),
  // Ganti metode pembayaran: { payment } transaksi baru, atau { order, paid } bila sudah dibayar.
  midtransRenew: (id) => api.post(`/orders/${id}/midtrans/renew`).then((r) => r.data),
};

export const WalletAPI = {
  get: () => api.get("/wallet").then((r) => r.data.wallet),
  topup: (amount, payment_method = "QRIS") => api.post("/wallet/topup", { amount, payment_method }).then((r) => r.data),
  transactions: () => api.get("/wallet/transactions").then((r) => r.data.transactions),
};

// Pembayaran manual (tanpa payment gateway): user upload bukti transfer, admin verifikasi.
export const UploadAPI = {
  // data = data URL base64 (idealnya sudah dikompres ke .webp <500KB di sisi klien)
  image: (filename, data) => api.post("/uploads", { filename, data }).then((r) => r.data.url),
};

export const PaymentAPI = {
  submitProof: (payload) => api.post("/payments/proof", payload).then((r) => r.data.confirmation),
  mine: () => api.get("/payments/mine").then((r) => r.data.confirmations),
  midtransConfig: () => api.get("/payments/midtrans/config").then((r) => r.data),
};

// Simpanan anggota (pokok/wajib/sukarela).
export const SavingsAPI = {
  mine: () => api.get("/savings/mine").then((r) => r.data),
  all: () => api.get("/savings").then((r) => r.data),
  // Setoran simpanan (WAJIB/SUKARELA) via bukti transfer → verifikasi admin keuangan.
  depositProof: (payload) => api.post("/savings/deposit-proof", payload).then((r) => r.data.confirmation),
  // Setoran simpanan via Midtrans: { payment: {token, redirect_url}, deposit } lalu cek status.
  depositMidtrans: (type, amount) => api.post("/savings/midtrans", { type, amount }).then((r) => r.data),
  depositMidtransSync: (id) => api.post(`/savings/midtrans/${id}/sync`).then((r) => r.data.deposit),
  // Penarikan simpanan sukarela.
  myWithdrawals: () => api.get("/savings/withdrawals/mine").then((r) => r.data.withdrawals),
  requestWithdrawal: (payload) => api.post("/savings/withdrawals", payload).then((r) => r.data.withdrawal),
};

// Pembagian Keuntungan Organisasi (CRUD kategori, admin) & SHU tracker anggota.
export const ShuAPI = {
  config: () => api.get("/shu/config").then((r) => r.data),
  updateConfig: (config) => api.put("/shu/config", { config }).then((r) => r.data),
  addCategory: (payload) => api.post("/shu/config", payload).then((r) => r.data.row),
  deleteCategory: (featureKey) => api.delete(`/shu/config/${encodeURIComponent(featureKey)}`).then((r) => r.data),
  mine: () => api.get("/shu/mine").then((r) => r.data),
};

// Portal penjual / gerai (merchant) — kelola pesanan yang masuk.
export const MerchantAPI = {
  shop: () => api.get("/merchant/shop").then((r) => r.data.shop),
  updateShop: (payload) => api.patch("/merchant/shop", payload).then((r) => r.data.shop),
  revenue: () => api.get("/merchant/revenue").then((r) => r.data),
  orders: () => api.get("/merchant/orders").then((r) => r.data.orders),
  incomingOrders: () => api.get("/merchant/incoming-orders").then((r) => r.data.orders),
  incomingOrder: (id) => api.get(`/merchant/incoming-orders/${id}`).then((r) => r.data.purchase_order),
  receiveIncoming: (id, note) => api.post(`/merchant/incoming-orders/${id}/receive`, { note }).then((r) => r.data),
  settleIncoming: (id, payload) => api.post(`/merchant/incoming-orders/${id}/settle`, payload).then((r) => r.data.settlement),
  accept: (id) => api.post(`/merchant/orders/${id}/accept`).then((r) => r.data),
  ship: (id, courier, tracking_number) => api.post(`/merchant/orders/${id}/ship`, { courier, tracking_number }).then((r) => r.data),
  registerShop: (payload) => api.post("/merchant/register-shop", payload).then((r) => r.data.shop),
  products: () => api.get("/merchant/products").then((r) => r.data.products),
  addProduct: (payload) => api.post("/merchant/products", payload).then((r) => r.data.product),
  // Verifikasi bukti transfer oleh penjual (pembeli membeli produk toko ini).
  paymentConfirmations: (status) => api.get("/merchant/payment-confirmations", { params: { status } }).then((r) => r.data.confirmations),
  approvePaymentProof: (id, note) => api.post(`/merchant/payment-confirmations/${id}/approve`, { note }).then((r) => r.data),
  rejectPaymentProof: (id, note) => api.post(`/merchant/payment-confirmations/${id}/reject`, { note }).then((r) => r.data),
  withdrawals: () => api.get("/merchant/withdrawals").then((r) => r.data.withdrawals),
  requestWithdrawal: (amount) => api.post("/merchant/withdrawals", { amount }).then((r) => r.data.withdrawal),
};

// Transaksi Offline O2O — scan QR gerai fisik lalu bayar via dompet.
export const O2OAPI = {
  resolve: (code) => api.get(`/o2o/resolve/${encodeURIComponent(code)}`).then((r) => r.data.merchant),
  pay: (payload) => api.post("/o2o/pay", payload).then((r) => r.data.receipt),
};

export const PublicAPI = {
  partners: () => api.get("/public/partners").then((r) => r.data.partners),
  provinces: () => api.get("/public/provinces").then((r) => r.data.provinces),
  regencies: (provinceId) => api.get("/public/regencies", { params: { province_id: provinceId } }).then((r) => r.data.regencies),
  branches: (regencyId) => api.get("/public/branches", { params: { regency_id: regencyId } }).then((r) => r.data.branches),
  cabang: (regencyId) => api.get("/public/cabang", { params: { regency_id: regencyId } }).then((r) => r.data.cabang),
  rantings: (cabangId) => api.get("/public/rantings", { params: { cabang_id: cabangId } }).then((r) => r.data.rantings),
  postalCodes: (regencyId) => api.get("/public/postal-codes", { params: { regency_id: regencyId } }).then((r) => r.data.postal_codes),
  news: () => api.get("/public/news").then((r) => r.data.news),
  newsDetail: (id) => api.get(`/public/news/${id}`).then((r) => r.data.news),
  stats: () => api.get("/public/stats").then((r) => r.data),
  homepage: () => api.get("/public/homepage").then((r) => r.data.homepage),
  organizations: (level) => api.get("/public/organizations", { params: level ? { level } : {} }).then((r) => r.data.organizations),
};

// Berita / artikel portal publik.
export const NewsAPI = {
  list: () => api.get("/public/articles").then((r) => r.data.articles),
  get: (idOrSlug) => api.get(`/public/articles/${idOrSlug}`).then((r) => r.data.article),
};

// Pengadaan — Surat Pesanan → Penerimaan Barang → Pembayaran.
export const ProcurementAPI = {
  list: () => api.get("/procurement/purchase-orders").then((r) => r.data.purchase_orders),
  get: (id) => api.get(`/procurement/purchase-orders/${id}`).then((r) => r.data.purchase_order),
  create: (payload) => api.post("/procurement/purchase-orders", payload).then((r) => r.data.purchase_order),
  receive: (id, payload) => api.post(`/procurement/purchase-orders/${id}/receive`, payload).then((r) => r.data),
  pay: (id, payload) => api.post(`/procurement/purchase-orders/${id}/pay`, payload).then((r) => r.data),
};

// Pendaftaran mandiri pengurus wilayah (Admin Daerah/Cabang/Ranting) — menunggu persetujuan berjenjang.
export const StaffAPI = {
  register: (payload) => api.post("/staff/register", payload).then((r) => r.data),
};

export const AdminAPI = {
  dashboard: () => api.get("/admin/dashboard").then((r) => r.data),
  members: () => api.get("/admin/members").then((r) => r.data.members),
  createMember: (payload) => api.post("/admin/members", payload).then((r) => r.data),
  memberDetail: (id) => api.get(`/admin/members/${id}`).then((r) => r.data),
  // Persentase Keuntungan Wilayah (6 level) — CRUD + riwayat versi
  revenueSplit: () => api.get("/admin/revenue-split").then((r) => r.data.split),
  revenueSplitHistory: () => api.get("/admin/revenue-split/history").then((r) => r.data.history),
  createRevenueSplit: (payload) => api.post("/admin/revenue-split", payload).then((r) => r.data.split),
  updateRevenueSplit: (payload) => api.put("/admin/revenue-split", payload).then((r) => r.data.split),
  deleteRevenueSplit: (id) => api.delete(`/admin/revenue-split/${id}`).then((r) => r.data),
  auditLogs: () => api.get("/admin/audit-logs").then((r) => r.data.logs),
  transactions: () => api.get("/admin/transactions").then((r) => r.data.transactions),
  reports: () => api.get("/admin/reports").then((r) => r.data),
  orders: () => api.get("/orders").then((r) => r.data.orders),
  updateOrderStatus: (id, status) => api.patch(`/orders/${id}/status`, { status }).then((r) => r.data),
  withdrawals: (status) => api.get("/admin/withdrawals", { params: { status } }).then((r) => r.data.withdrawals),
  approveWithdrawal: (id, note) => api.post(`/admin/withdrawals/${id}/approve`, { note }).then((r) => r.data),
  rejectWithdrawal: (id, note) => api.post(`/admin/withdrawals/${id}/reject`, { note }).then((r) => r.data),
  shops: (status) => api.get("/admin/shops", { params: { status } }).then((r) => r.data.shops),
  shopAction: (id, action) => api.post(`/admin/shops/${id}/${action}`).then((r) => r.data),
  investorReport: () => api.get("/admin/investor-report").then((r) => r.data),
  staffRegistrations: (status) => api.get("/admin/staff-registrations", { params: { status } }).then((r) => r.data.registrations),
  approveStaff: (id) => api.post(`/admin/staff-registrations/${id}/approve`).then((r) => r.data),
  rejectStaff: (id, note) => api.post(`/admin/staff-registrations/${id}/reject`, { note }).then((r) => r.data),
  roles: () => api.get("/admin/roles").then((r) => r.data.roles),
  cashflow: () => api.get("/admin/cashflow").then((r) => r.data),
  updateHomepage: (homepage) => api.put("/admin/homepage", { homepage }).then((r) => r.data.homepage),
  news: () => api.get("/admin/news").then((r) => r.data.news),
  createNews: (payload) => api.post("/admin/news", payload).then((r) => r.data.news),
  updateNews: (id, payload) => api.put(`/admin/news/${id}`, payload).then((r) => r.data.news),
  deleteNews: (id) => api.delete(`/admin/news/${id}`).then((r) => r.data),
  paymentConfirmations: (status) => api.get("/admin/payment-confirmations", { params: { status } }).then((r) => r.data.confirmations),
  approvePayment: (id, note) => api.post(`/admin/payment-confirmations/${id}/approve`, { note }).then((r) => r.data),
  rejectPayment: (id, note) => api.post(`/admin/payment-confirmations/${id}/reject`, { note }).then((r) => r.data),
  // Penarikan simpanan sukarela anggota (verifikasi + konfirmasi admin keuangan)
  savingsWithdrawals: (status) => api.get("/admin/savings-withdrawals", { params: { status } }).then((r) => r.data.withdrawals),
  approveSavingsWithdrawal: (id, note) => api.post(`/admin/savings-withdrawals/${id}/approve`, { note }).then((r) => r.data),
  confirmSavingsWithdrawal: (id) => api.post(`/admin/savings-withdrawals/${id}/confirm`).then((r) => r.data),
  rejectSavingsWithdrawal: (id, note) => api.post(`/admin/savings-withdrawals/${id}/reject`, { note }).then((r) => r.data),
  goodsSettlements: (status) => api.get("/admin/goods-settlements", { params: { status } }).then((r) => r.data.settlements),
  approveSettlement: (id, note) => api.post(`/admin/goods-settlements/${id}/approve`, { note }).then((r) => r.data),
  rejectSettlement: (id, note) => api.post(`/admin/goods-settlements/${id}/reject`, { note }).then((r) => r.data),
  // RBAC — matriks izin & peran kustom
  rbacCatalog: () => api.get("/admin/rbac/catalog").then((r) => r.data),
  rbacPermissions: (role) => api.get("/admin/rbac/permissions", { params: { role } }).then((r) => r.data),
  saveRbacPermissions: (role, permissions) => api.put("/admin/rbac/permissions", { role, permissions }).then((r) => r.data),
  createRole: (payload) => api.post("/admin/rbac/roles", payload).then((r) => r.data),
  // Sumber data unduhan laporan
  shuReport: () => api.get("/admin/reports/shu").then((r) => r.data.shu),
  stockReport: () => api.get("/admin/reports/stock").then((r) => r.data.stock),
  // Manajemen wilayah (organisasi)
  organizationsManage: () => api.get("/admin/organizations").then((r) => r.data.organizations),
  createOrganization: (payload) => api.post("/admin/organizations", payload).then((r) => r.data.organization),
  updateOrganization: (id, payload) => api.put(`/admin/organizations/${id}`, payload).then((r) => r.data.organization),
  deleteOrganization: (id) => api.delete(`/admin/organizations/${id}`).then((r) => r.data),
};
